import * as THREE from 'three';
import { Renderer } from './Renderer';
import { Environment, createTerrainMaterial } from './Environment';
import { EntityManager } from './Entities';
import { ScenarioManager } from './Scenario';
import { ToolSystem } from './ToolSystem';
import { InputController } from './Input';
import { UIState } from '../ui/store';
import { AssetManager, ASSET_URLS } from './Assets';
import { PPE_ITEMS } from '../ui/hud';
import { DevEditor } from './DevEditor';
import { OhlecManager } from './OhlecManager';

export class App {
  public renderer: Renderer;
  public environment: Environment;
  public entities: EntityManager;
  public scenario?: ScenarioManager;
  public tools?: ToolSystem;
  public input?: InputController;
  public devEditor?: DevEditor;
  public ohlecManager?: OhlecManager;

  private isRunning: boolean = false;
  private ppeGroup = new THREE.Group();

  private clock = new THREE.Clock();

  constructor(canvasId: string) {
    this.renderer = new Renderer(canvasId);
    this.environment = new Environment(this.renderer.scene);
    this.entities = new EntityManager(this.renderer.scene);

    // Setup PPE Scene (located far above main scene)
    this.ppeGroup.position.set(0, 1000, 0);
    const manModel = AssetManager.getModel(ASSET_URLS.MODELS.MAN);
    if (manModel) {
      manModel.scene.scale.set(1.06, 1.06, 1.06);
      this.ppeGroup.add(manModel.scene);
    }
    this.renderer.scene.add(this.ppeGroup);

    this.setupPPETerrain();

    if (new URLSearchParams(window.location.search).get('editMode') === 'true') {
      this.devEditor = new DevEditor(
        this.renderer.scene,
        this.renderer.camera,
        this.renderer.renderer,
        this.renderer.controls
      );
    }

    // Subscribe to UI State for Telemetry and Debugging
    let lastActiveMode: string | null = null;

    UIState.subscribe((state) => {
      // Equip/unequip PPE objects on character model
      PPE_ITEMS.forEach(item => {
        if (!item.models || item.models.length === 0) return;

        const isEquipped = !!state.equippedPPE[item.id];

        item.models.forEach((modelConfig, index) => {
          const meshName = `equipped-${item.id}-${index}`;
          let ppeMesh = this.ppeGroup.getObjectByName(meshName);

          if (isEquipped) {
            if (!ppeMesh) {
              const ppeModel = AssetManager.getModel(modelConfig.modelPath);
              if (ppeModel) {
                ppeMesh = ppeModel.scene.clone();
                ppeMesh.name = meshName;

                if (modelConfig.manPosition) {
                  ppeMesh.position.set(modelConfig.manPosition[0], modelConfig.manPosition[1], modelConfig.manPosition[2]);
                }
                if (modelConfig.manRotation) {
                  ppeMesh.rotation.set(modelConfig.manRotation[0], modelConfig.manRotation[1], modelConfig.manRotation[2]);
                }
                if (modelConfig.manScale) {
                  ppeMesh.scale.set(modelConfig.manScale[0], modelConfig.manScale[1], modelConfig.manScale[2]);
                }

                this.ppeGroup.add(ppeMesh);
              }
            }
            if (ppeMesh) {
              ppeMesh.visible = true;
            }
          } else {
            if (ppeMesh) {
              ppeMesh.visible = false;
            }
          }
        });
      });

      if (this.scenario) {
        this.scenario.setTelemetry(state.showPerfWarnings);
        this.scenario.setGravity(state.simulation.gravity);
        this.scenario.setShowTension(state.featureFlags.showTension);
      }

      this.applyDebugStyles(state);

      if (state.activeMode !== lastActiveMode) {
        lastActiveMode = state.activeMode;

        if (state.activeMode === 'PPE') {
          this.stopSimulator();
          if (this.devEditor) this.devEditor.setTargetGroup(this.ppeGroup);
          this.ppeGroup.visible = true;
          // Move camera to PPE scene
          this.renderer.camera.position.set(0, 1002, 4);
          this.renderer.controls.target.set(0, 1001, 0);
          this.renderer.controls.update();
          // Move directional light to PPE scene
          this.renderer.dirLight.position.set(10, 1020, 10);
          this.renderer.dirLight.target.position.set(0, 1000, 0);
          this.renderer.dirLight.target.updateMatrixWorld();
          this.renderer.scene.updateMatrixWorld(true);
        } else if (state.activeMode === 'OHLEC' || state.activeMode === 'ASSESSMENT') {
          this.stopSimulator();
          if (this.devEditor) this.devEditor.setTargetGroup(null);
          this.ppeGroup.visible = false;
          // Move camera to an empty canvas position
          this.renderer.camera.position.set(0, 2, 4);
          this.renderer.controls.target.set(0, 0.5, 0);
          this.renderer.controls.update();
          // Move directional light back to main scene
          this.renderer.dirLight.position.set(10, 20, 10);
          this.renderer.dirLight.target.position.set(0, 0, 0);
          this.renderer.dirLight.target.updateMatrixWorld();
          this.renderer.scene.updateMatrixWorld(true);
        } else { // SIMULATOR
          if (this.devEditor) this.devEditor.setTargetGroup(null);
          this.ppeGroup.visible = false;
          // Move camera back to main scene if it was in PPE
          if (this.renderer.camera.position.y > 500) {
            this.renderer.camera.position.set(0, 2, 4);
            this.renderer.controls.target.set(0, 0.5, 0);
            this.renderer.controls.update();
          }
          // Move directional light back to main scene
          this.renderer.dirLight.position.set(10, 20, 10);
          this.renderer.dirLight.target.position.set(0, 0, 0);
          this.renderer.dirLight.target.updateMatrixWorld();
          this.renderer.scene.updateMatrixWorld(true);
          this.startSimulator();
        }
      }

      if (state.activeTool === 'hand') {
        this.renderer.renderer.domElement.style.cursor = 'grab';
      } else if (state.activeTool === 'wedge') {
        this.renderer.renderer.domElement.style.cursor = 'crosshair';
      } else {
        this.renderer.renderer.domElement.style.cursor = 'default';
      }

      if (state.chainsawStuck && this.tools && this.input) {
        if (this.tools.throttleOn) {
          this.tools.setThrottle(false);
        }
        this.input.forceDetachGizmo();
      }
    });
  }

  private setupPPETerrain() {
    const size = 100;
    const segments = 200;
    const groundGeo = new THREE.PlaneGeometry(size, size, segments, segments);

    // Rotate to be horizontal
    groundGeo.rotateX(-Math.PI / 2);

    const pathIntensities = new Float32Array((segments + 1) * (segments + 1));
    const halfSize = size / 2;
    const step = size / segments;

    let index = 0;
    for (let i = 0; i <= segments; i++) {
      const z = -halfSize + i * step;
      const pathX = Math.sin(z * 0.1) * 3.0 + Math.sin(z * 0.03) * 5.0;
      for (let j = 0; j <= segments; j++) {
        const x = -halfSize + j * step;

        const distToPath = Math.abs(x - pathX);
        const pathWidth = 2.0;
        const fade = 4.0;
        let intensity = 0;
        if (distToPath < pathWidth) {
          intensity = 1.0;
        } else if (distToPath < pathWidth + fade) {
          intensity = 1.0 - (distToPath - pathWidth) / fade;
          intensity = intensity * intensity * (3.0 - 2.0 * intensity); // Smoothstep
        }
        pathIntensities[index] = intensity;

        index++;
      }
    }

    groundGeo.computeVertexNormals();
    groundGeo.setAttribute('pathIntensity', new THREE.BufferAttribute(pathIntensities, 1));

    const groundMat = createTerrainMaterial();

    const groundMesh = new THREE.Mesh(groundGeo, groundMat);
    groundMesh.receiveShadow = true;

    this.ppeGroup.add(groundMesh);
  }

  private applyDebugStyles(state: any) {
    if (this.tools) {
      if (this.tools.guidebarBox) {
        this.tools.guidebarBox.visible = !!state.featureFlags.showGuidebarBox;
      }
      this.tools.showKerfBoxDebug = !!state.featureFlags.showKerfBox;
    }

    const targetVisible = !state.featureFlags.showEdges && !state.featureFlags.showNodes;
    const targetShowTension = !!state.featureFlags.showTension;

    for (const logEntity of this.entities.getAll()) {
      if (logEntity.type === 'log' && logEntity.mesh instanceof THREE.Mesh) {
        const materials = Array.isArray(logEntity.mesh.material) ? logEntity.mesh.material : [logEntity.mesh.material];

        for (const mat of materials) {
          if (mat.userData.originalMap === undefined) {
            mat.userData.originalMap = mat.map;
          }

          const targetMap = targetShowTension ? null : mat.userData.originalMap;

          let changed = false;

          if (mat.visible !== targetVisible) {
            mat.visible = targetVisible;
            changed = true;
          }

          if (mat.map !== targetMap) {
            mat.map = targetMap;
            changed = true;
          }

          if (mat.vertexColors !== targetShowTension) {
            mat.vertexColors = targetShowTension;
            if (targetShowTension) {
              mat.color.setHex(0xffffff);
            }
            changed = true;
          }

          if (changed) {
            mat.needsUpdate = true;
          }
        }

        if (logEntity.points) {
          const ptsMat = logEntity.points.material as THREE.Material;
          const targetNodesVisible = !!state.featureFlags.showNodes;
          const targetDepthTest = !!state.featureFlags.depthTest;

          if (logEntity.points.visible !== targetNodesVisible) {
            logEntity.points.visible = targetNodesVisible;
          }
          if (ptsMat.depthTest !== targetDepthTest) {
            ptsMat.depthTest = targetDepthTest;
            ptsMat.needsUpdate = true;
          }
        }
        if (logEntity.wireframe) {
          logEntity.wireframe.visible = false; // We use global springs mesh instead
          const wireMat = logEntity.wireframe.material as THREE.Material;
          const targetDepthTest = !!state.featureFlags.depthTest;
          if (wireMat.depthTest !== targetDepthTest) {
            wireMat.depthTest = targetDepthTest;
            wireMat.needsUpdate = true;
          }
        }
      }
    }

    if (this.entities.globalSpringsMesh) {
      const mesh = this.entities.globalSpringsMesh;
      const springsMat = mesh.material as THREE.Material;
      const targetEdgesVisible = !!state.featureFlags.showEdges;
      const targetDepthTest = !!state.featureFlags.depthTest;

      if (mesh.visible !== targetEdgesVisible) {
        mesh.visible = targetEdgesVisible;
      }
      if (springsMat.depthTest !== targetDepthTest) {
        springsMat.depthTest = targetDepthTest;
        springsMat.needsUpdate = true;
      }
    }
  }

  public init() {
    // Environment is now generated inside loadScenario
  }

  public startSimulator() {
    if (this.scenario) return; // already running
    console.log('[App] Starting Simulator...');

    // 1. Scenario (Web Worker IPC)
    this.scenario = new ScenarioManager(
      (positions, strains, brokenKerfSprings, wedges) => {
        for (const entity of this.entities.getAll()) {
          if (entity.type === 'log') {
            this.entities.syncPositions(entity.id, positions, strains, brokenKerfSprings, UIState.getState().featureFlags.globalTension);
          }
        }
        if (this.tools && wedges) {
          this.tools.syncWedges(wedges);
        }
      },
      (segments, kerfs, springs, positions) => {
        this.entities.syncTopology(segments, kerfs, springs, positions);
        this.applyDebugStyles(UIState.getState());
      },
      this.environment,
      (scenario) => {
        this.resetSceneAndUI(scenario);
      }
    );

    // 2. Tools
    this.tools = new ToolSystem(
      this.entities,
      (point, normal) => {
        this.scenario?.sendCutEvent(point, normal);
      },
      (pos, quat, scale) => {
        this.scenario?.sendCutProgress(pos, quat, scale);
      }
    );

    // 3. Input
    this.input = new InputController(
      this.renderer.scene,
      this.renderer.camera,
      this.renderer,
      this.tools,
      this.entities,
      this.scenario
    );

    // Push current UI state down to newly created systems
    const state = UIState.getState();
    this.scenario.setTelemetry(state.showPerfWarnings);
    this.scenario.setGravity(state.simulation.gravity);
    this.scenario.setShowTension(state.featureFlags.showTension);
    this.applyDebugStyles(state);

    // Initial load
    this.scenario.loadScenario('/assets/scenarios/top_bind.json');
  }

  public startOhlecMode() {
    if (this.scenario) return;
    console.log('[App] Starting OHLEC Mode...');

    this.ohlecManager = new OhlecManager(this.renderer, this.environment, this.entities);

    // 1. Scenario (Web Worker IPC)
    this.scenario = new ScenarioManager(
      (positions, strains, brokenKerfSprings) => {
        for (const entity of this.entities.getAll()) {
          if (entity.type === 'log') {
            this.entities.syncPositions(entity.id, positions, strains, brokenKerfSprings, UIState.getState().featureFlags.globalTension);
          }
        }
      },
      (segments, kerfs, springs, positions) => {
        this.entities.syncTopology(segments, kerfs, springs, positions);
        this.applyDebugStyles(UIState.getState());
      },
      this.environment,
      (scenario) => {
        this.resetSceneAndUI(scenario);
      }
    );

    // No ToolSystem or InputController instantiated for OHLEC mode
    // to enforce the "hands-off" requirement before cuts are planned.
    this.tools = undefined;
    this.input = undefined;

    // Set UI overrides for OHLEC
    const state = UIState.getState();
    this.scenario.setTelemetry(state.showPerfWarnings);
    this.scenario.setGravity(state.simulation.gravity);
    this.scenario.setShowTension(state.featureFlags.showTension);
    this.applyDebugStyles(state);

    // Initial load for OHLEC
    this.scenario.loadScenario('/assets/scenarios/ohlec_general.json');
  }

  public stopSimulator() {
    if (!this.scenario) return;
    console.log('[App] Stopping Simulator...');

    if (this.ohlecManager) {
      this.ohlecManager.dispose();
      this.ohlecManager = undefined;
    }

    this.scenario.terminate();
    this.scenario = undefined;

    this.entities.clearLogs();
    this.entities.clearWedges();
    this.environment.clear();

    // Remove tool meshes
    for (const e of this.entities.getAll()) {
      if (e.type === 'tool' || e.type === 'wedge') {
        this.entities.remove(e.id);
      }
    }
    this.tools = undefined;

    if (this.input) {
      this.input.dispose();
      this.input = undefined;
    }
  }

  public loadScenario(id: string) {
    if (this.scenario) {
      this.scenario.loadScenario(id);
    }
  }

  public loadScenarioObj(obj: any) {
    if (this.scenario) {
      this.scenario.loadScenarioObj(obj);
    }
  }

  public resetSceneAndUI(scenario: any) {
    console.log('[App] Resetting scene and UI for new scenario...');

    // Clear Environment terrain
    this.environment.clear();

    // Clear log entities
    this.entities.clearLogs();
    this.entities.clearWedges();

    // Reset tools (chainsaw alignment)
    if (this.tools) {
      this.tools.resetToScenario(scenario.chainsaw);
    }

    // Reset input (hand force)
    if (this.input) {
      this.input.reset();
    }

    // Reset OHLEC Manager if active
    if (this.ohlecManager) {
      this.ohlecManager.init(scenario);
    }

    // Reset global UI state
    UIState.update({
      activeTool: 'chainsaw',
      throttleOn: false,
      chainsawStuck: false,
      lastResetTime: performance.now()
    });
    UIState.updateSimulation('currentPressure', 0);

    // Send immediate updated position so worker drops binding pressure
    if (this.tools && this.scenario) {
      const guidebarData = this.tools.getGuidebarTransform();
      if (guidebarData) {
        this.scenario.sendGuidebarState(true, guidebarData.pos, guidebarData.quat, guidebarData.scale);
      }
    }

    // Reset DOM elements
    const kerfFill = document.getElementById('kerf-fill');
    if (kerfFill) {
      kerfFill.style.width = '0%';
    }
    const stuckModal = document.getElementById('modal-stuck');
    if (stuckModal) {
      stuckModal.classList.add('hidden');
    }
  }

  public run() {
    if (this.isRunning) return;
    this.isRunning = true;
    this.loop();
  }

  public stop() {
    this.isRunning = false;
    this.stopSimulator();
  }

  private loop = () => {
    if (!this.isRunning) return;

    requestAnimationFrame(this.loop);

    const dt = this.clock.getDelta();

    // Update tools (animate chain)
    if (this.tools) {
      this.tools.tick(dt);

      const guidebarData = this.tools.getGuidebarTransform();
      const isSawInUseOrStuck = UIState.getState().activeTool === 'chainsaw' || UIState.getState().chainsawStuck || this.tools.isCutting;
      if (guidebarData && isSawInUseOrStuck) {
        this.scenario?.sendGuidebarState(true, guidebarData.pos, guidebarData.quat, guidebarData.scale);
      } else {
        this.scenario?.sendGuidebarState(false);
      }
    }

    // Update OHLEC Manager if active (shake animations, etc.)
    if (this.ohlecManager) {
      this.ohlecManager.update(dt);
    }

    // Update Renderer (which updates OrbitControls)
    this.renderer.update();

    // Render Scene
    this.renderer.render();
  }
}
